/**
 * Placing a world coordinate on a vendor map image. PURE.
 *
 * `map_fetch` returns an image plus `originPosition`: width and height in PIXELS,
 * originX and originY in METRES. What it does NOT return is the resolution, and both
 * halves of this transform need it.
 *
 * THE RESOLUTION IS AN ASSUMPTION AND IS CARRIED AS ONE. `keenon-api-reference.md` 4.2a
 * records it as empirically 0.05 m/px, pending verification against a known dock-to-dock
 * distance. A wrong resolution does not look broken: it puts the robot in a plausible
 * place on a map of the wrong size, which is the hardest kind of wrong to see. So every
 * result of this module says which resolution produced it and whether that was measured
 * or guessed, and the UI is expected to show it.
 *
 * The pixel convention is `map_server`'s, which is what 4.2a says the palette follows:
 * the origin is the BOTTOM-LEFT corner in world coordinates, while pixel (0,0) is the
 * TOP-LEFT. Hence the y flip. Getting that wrong mirrors the map vertically, which also
 * looks plausible.
 */

/**
 * 0.05 m/px, DERIVED from two live maps — see keenon-api-reference.md 4.2a. The vendor
 * sends no resolution, so this is the best available value and not a measurement.
 *
 * HOW SOLID IT IS, PER AXIS, because the two differ and the difference matters:
 *
 *   * **resY = 0.05 is forced.** The y origins are 1195 and 2120 cm (GCD 5 cm) so it
 *     must divide 0.05, and the origin must land inside its own image so it must be
 *     >= 0.0271. One candidate.
 *   * **resX is only narrowed to {0.05, 0.10}.** The x origins are 2290 and 830 cm, and
 *     their GCD is 10 cm, so the arithmetic permits either. 0.10 is excluded because it
 *     would make those two buildings 48 m and 39 m across, against operator estimates of
 *     ~15 m and ~20 m — a JUDGEMENT about plausibility, not a proof.
 *
 * So SQUARE PIXELS ARE AN ASSUMPTION resting on a size estimate, which is why this module
 * carries the axes separately and why the panel lets them be overridden: the fastest way
 * to settle it is one tape measure across a wall.
 */
export const ASSUMED_RESOLUTION_M_PER_PX = 0.05;

/** Where a resolution came from, so the UI never presents a guess as a measurement. */
export type ResSource = "vendor" | "derived" | "manual";

export interface MapFrame {
  /** Image size in pixels, from `originPosition` (cross-checked against the PNG). */
  width: number;
  height: number;
  /** World coordinates of the image's bottom-left corner, in metres. */
  originX: number;
  originY: number;
  /** Metres per pixel, PER AXIS. Separate because squareness is an assumption here, not
   *  a fact — see ASSUMED_RESOLUTION_M_PER_PX. Equal by default. */
  resX: number;
  resY: number;
  /** `vendor` if it ever sends one, `derived` for the default, `manual` when somebody
   *  has overridden it to compare against a measurement. The UI must show this: silence
   *  would present a guess as a measurement. */
  resSource: ResSource;
}

/** True when both axes share a scale — which is the normal case and also the thing we
 *  cannot yet prove for this vendor. */
export const isSquare = (f: MapFrame): boolean => f.resX === f.resY;

/** A copy with a different scale, for comparing against a measured distance. */
export function withResolution(f: MapFrame, resX: number, resY: number): MapFrame {
  return { ...f, resX, resY, resSource: "manual" };
}

/**
 * The resolution implied by measuring a real distance across a known pixel span.
 *
 * This is the whole point of showing pixel counts in the UI: a pixel span is a FACT
 * about the image, so one tape measure turns it into a resolution with no inference at
 * all. Returns null rather than Infinity for a zero span.
 */
export function impliedResolution(pixels: number, metres: number): number | null {
  if (!Number.isFinite(pixels) || !Number.isFinite(metres) || pixels <= 0) return null;
  return metres / pixels;
}

/** Read a frame out of a `map_fetch` answer's metadata, or null if it is not there. */
export function readMapFrame(data: unknown): MapFrame | null {
  const op = findOriginPosition(data);
  if (!op) return null;
  const num = (v: unknown): number | null => {
    const n = typeof v === "string" ? Number(v) : v;
    return typeof n === "number" && Number.isFinite(n) ? n : null;
  };
  const width = num(op.width);
  const height = num(op.height);
  const originX = num(op.originX);
  const originY = num(op.originY);
  // All four or nothing. Defaulting a missing origin to 0 would place the map somewhere
  // specific and wrong, which is worse than declining to place it at all.
  if (width === null || height === null || originX === null || originY === null) return null;
  if (width <= 0 || height <= 0) return null;

  const declared = num(op.resolution);          // never seen, but cost-free to honour
  const res = declared && declared > 0 ? declared : ASSUMED_RESOLUTION_M_PER_PX;
  return {
    width, height, originX, originY,
    resX: res, resY: res,
    resSource: declared && declared > 0 ? "vendor" : "derived",
  };
}

function findOriginPosition(node: unknown, seen = new Set<unknown>()):
    Record<string, unknown> | null {
  if (!node || typeof node !== "object" || seen.has(node)) return null;
  seen.add(node);
  if (Array.isArray(node)) {
    for (const n of node) {
      const hit = findOriginPosition(n, seen);
      if (hit) return hit;
    }
    return null;
  }
  const o = node as Record<string, unknown>;
  const op = o.originPosition;
  if (op && typeof op === "object" && !Array.isArray(op)) return op as Record<string, unknown>;
  // Tolerated at the top level too: the adapter publishes the vendor's metadata verbatim
  // minus the image, and no response has ever been captured, so the nesting is unproven.
  if ("originX" in o && "width" in o) return o;
  for (const v of Object.values(o)) {
    const hit = findOriginPosition(v, seen);
    if (hit) return hit;
  }
  return null;
}

export interface Px { px: number; py: number }

/**
 * World metres -> image pixels. THE ONE DEFINITION of this convention.
 *
 * `originPosition` IS THE TOP-RIGHT CORNER, and both axes run opposite to the image's.
 * The image is 180 degrees rotated from the `map_server` convention — which is NOT what
 * 4.2a's palette note would lead you to assume, and not what this originally did.
 *
 * HOW IT WAS SETTLED, because it is not guessable and the wrong answer looks plausible:
 * `map_points_v2` gives the charging pile in IMAGE PIXELS, and a SLAM origin is normally
 * where mapping started — the dock. Mapping world (0,0) under each of the four possible
 * corner conventions and measuring to that pile:
 *
 *     origin bottom-left  (map_server, what this used to do)   9.39 m away
 *     origin top-left     (no y flip)                          5.90 m away
 *     origin bottom-right (x reversed)                         7.30 m away
 *     origin TOP-RIGHT    (both reversed)                      0.21 m away
 *
 * Four pixels. The other three are metres out. Reported first as "it moves left on the
 * map while moving right in the room, almost 180 degrees but not quite" — exactly what a
 * half-corrected transform looks like, because flipping y alone is a MIRROR rather than
 * a rotation.
 *
 * Returns a point even when it falls OUTSIDE the image: clamping would silently move a
 * robot onto the map, and a robot drawn at the edge when it is really off it is a lie
 * the operator cannot detect. `inBounds` lets the caller say so instead.
 */
export function worldToPixel(f: MapFrame, x: number, y: number): Px {
  return {
    px: f.width - (x - f.originX) / f.resX,
    py: (y - f.originY) / f.resY,
  };
}

/** Image pixels -> world metres. The exact inverse of `worldToPixel`. */
export function pixelToWorld(f: MapFrame, px: number, py: number) {
  return { mx: f.originX + (f.width - px) * f.resX,
           my: f.originY + py * f.resY };
}

/**
 * A world heading as a direction on the CANVAS, unit length.
 *
 * Both axes are reversed relative to the image, so a heading does not simply carry over:
 * x is negated by the reversal, and y is NOT negated — the usual "screen y grows
 * downward" correction and the axis reversal cancel. Getting this wrong points every
 * robot's nose the wrong way, which reads as a robot driving backwards.
 */
export function headingOnCanvas(yaw: number): { dx: number; dy: number } {
  return { dx: -Math.cos(yaw), dy: Math.sin(yaw) };
}

export function inBounds(f: MapFrame, p: Px): boolean {
  return p.px >= 0 && p.px <= f.width && p.py >= 0 && p.py <= f.height;
}

/** The world extent of the whole CANVAS. Not a useful sanity check on its own — see
 *  `occupiedExtent`, which is. */
export function extentMetres(f: MapFrame): { w: number; h: number } {
  return { w: f.width * f.resX, h: f.height * f.resY };
}

/**
 * The extent of the WALLS — the only number worth checking against a real building.
 *
 * The canvas is much bigger than the space. On the two live maps the drawn walls occupy
 * roughly a third of the image area, so "the canvas is 40 m across" was a misleading
 * check that made a correct resolution look wrong. This measures the occupied pixels
 * instead, which is what someone standing in the room can actually compare against.
 *
 * `occupied` is 0 in the map_server palette. NOTE that 205 (unknown) does not appear in
 * these maps at all: every pixel is free or occupied, so this is a cleaned map rather
 * than a raw SLAM grid, and there is no unmapped region to exclude.
 *
 * Returns PIXELS ONLY, and takes no frame. The measurement is a fact about the image and
 * must not be stored alongside a metre figure computed from whatever scale happened to be
 * in force when the image loaded — the scale is editable, so a cached conversion would go
 * quietly stale the moment somebody tried a different one. Callers multiply at the point
 * of display.
 *
 * Takes the raw RGBA bytes rather than an ImageData so it stays testable without a DOM.
 */
export function occupiedExtentPx(
  rgba: Uint8ClampedArray | number[], width: number, height: number,
): { wPx: number; hPx: number; pixels: number } | null {
  let minX = width, maxX = -1, minY = height, maxY = -1, pixels = 0;
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      // Red channel only: every pixel in these maps is grey (r === g === b), verified
      // across both live images, so one channel is the whole story.
      if (rgba[(y * width + x) * 4] !== 0) continue;
      pixels++;
      if (x < minX) minX = x;
      if (x > maxX) maxX = x;
      if (y < minY) minY = y;
      if (y > maxY) maxY = y;
    }
  }
  if (maxX < 0) return null;            // nothing occupied: an empty or unreadable map
  return { wPx: maxX - minX + 1, hPx: maxY - minY + 1, pixels };
}
